export interface Client {
  id: string;
  name: string;
  email: string;
  company?: string;
  address?: string;
  phone?: string;
  abn?: string;
  paymentTerms?: string;
  createdAt: string;
}

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  date?: string; // optional per-item date (ISO YYYY-MM-DD)
}

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue';

export interface Invoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientCompany?: string;
  clientAddress?: string;
  clientAbn?: string;
  // Sender details
  senderName: string;
  senderEmail: string;
  senderCompany?: string;
  senderAddress?: string;
  senderAbn?: string;
  issueDate: string;
  dueDate: string;
  lineItems: LineItem[];
  notes?: string;
  paymentInstructions?: string;
  status: InvoiceStatus;
  subtotal: number;
  tax: number;
  taxRate: number;
  gst: number;
  gstRate: number;
  gstInclusive?: boolean;
  total: number;
  createdAt: string;
  updatedAt: string;
}

export interface SenderProfile {
  name: string;
  email: string;
  company?: string;
  address?: string;
  abn?: string;
}

export interface InvoiceTemplate {
  id: string;
  name: string;
  description?: string;
  // Branding
  companyName?: string;
  companyLogo?: string;
  accentColor?: string;
  secondaryColor?: string;
  // Sender details
  senderName: string;
  senderEmail: string;
  senderCompany?: string;
  senderAddress?: string;
  senderAbn?: string;
  // Default client ABN (pre-fill hint)
  defaultClientAbn?: string;
  // Layout
  invoiceLayout?: 'classic' | 'modern' | 'minimal';
  // Defaults
  taxRate: number;
  gstRate: number;
  notes?: string;
  paymentInstructions?: string;
  // Pre-filled line item categories
  lineItems: LineItem[];
  createdAt: string;
  updatedAt: string;
}

const INVOICES_KEY = 'freelance_invoices';
const CLIENTS_KEY = 'freelance_clients';
const SENDER_KEY = 'freelance_sender_profile';
const TEMPLATES_KEY = 'freelance_invoice_templates';

// --- Invoice Storage ---
export function getInvoices(): Invoice[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(INVOICES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveInvoice(invoice: Invoice): void {
  try {
    const invoices = getInvoices();
    const idx = invoices.findIndex((i) => i.id === invoice.id);
    if (idx >= 0) {
      invoices[idx] = invoice;
    } else {
      invoices.unshift(invoice);
    }
    localStorage.setItem(INVOICES_KEY, JSON.stringify(invoices));
  } catch (err) {
    console.error('[storage] saveInvoice failed:', err);
    throw err;
  }
}

export function deleteInvoice(id: string): void {
  try {
    const invoices = getInvoices().filter((i) => i.id !== id);
    localStorage.setItem(INVOICES_KEY, JSON.stringify(invoices));
  } catch (err) {
    console.error('[storage] deleteInvoice failed:', err);
  }
}

export function getInvoiceById(id: string): Invoice | undefined {
  return getInvoices().find((i) => i.id === id);
}

// --- Client Storage ---
export function getClients(): Client[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(CLIENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveClient(client: Client): void {
  try {
    const clients = getClients();
    const idx = clients.findIndex((c) => c.id === client.id);
    if (idx >= 0) {
      clients[idx] = client;
    } else {
      clients.unshift(client);
    }
    localStorage.setItem(CLIENTS_KEY, JSON.stringify(clients));
  } catch (err) {
    console.error('[storage] saveClient failed:', err);
    throw err;
  }
}

export function deleteClient(id: string): void {
  try {
    const clients = getClients().filter((c) => c.id !== id);
    localStorage.setItem(CLIENTS_KEY, JSON.stringify(clients));
  } catch (err) {
    console.error('[storage] deleteClient failed:', err);
  }
}

// --- Sender Profile Storage ---
export function getSenderProfile(): SenderProfile {
  if (typeof window === 'undefined') return { name: '', email: '' };
  try {
    const raw = localStorage.getItem(SENDER_KEY);
    return raw ? JSON.parse(raw) : { name: '', email: '' };
  } catch {
    return { name: '', email: '' };
  }
}

export function saveSenderProfile(profile: SenderProfile): void {
  try {
    localStorage.setItem(SENDER_KEY, JSON.stringify(profile));
  } catch (err) {
    console.error('[storage] saveSenderProfile failed:', err);
  }
}

// --- Template Storage ---
export function getTemplates(): InvoiceTemplate[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveTemplate(template: InvoiceTemplate): void {
  try {
    const templates = getTemplates();
    const idx = templates.findIndex((t) => t.id === template.id);
    if (idx >= 0) {
      templates[idx] = template;
    } else {
      templates.unshift(template);
    }
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  } catch (err) {
    console.error('[storage] saveTemplate failed:', err);
    throw err;
  }
}

export function deleteTemplate(id: string): void {
  try {
    const templates = getTemplates().filter((t) => t.id !== id);
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  } catch (err) {
    console.error('[storage] deleteTemplate failed:', err);
  }
}

export function getTemplateById(id: string): InvoiceTemplate | undefined {
  return getTemplates().find((t) => t.id === id);
}

// --- Helpers ---
export function generateId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export function generateInvoiceNumber(invoices: Invoice[]): string {
  if (!Array.isArray(invoices) || invoices.length === 0) return 'INV-0001';
  const max = invoices.reduce((acc, inv) => {
    const num = parseInt((inv.invoiceNumber ?? '').replace(/\D/g, ''), 10);
    return isNaN(num) ? acc : Math.max(acc, num);
  }, 0);
  return `INV-${String(max + 1).padStart(4, '0')}`;
}

export function calcTotals(lineItems: LineItem[], taxRate: number, gstRate: number) {
  const safeItems = Array.isArray(lineItems) ? lineItems : [];
  const safeTaxRate = isNaN(taxRate) || taxRate < 0 ? 0 : taxRate;
  const safeGstRate = isNaN(gstRate) || gstRate < 0 ? 0 : gstRate;
  const subtotal = safeItems.reduce((sum, item) => {
    const qty = isNaN(item?.quantity) ? 0 : (item?.quantity ?? 0);
    const rate = isNaN(item?.rate) ? 0 : (item?.rate ?? 0);
    return sum + qty * rate;
  }, 0);
  const tax = subtotal * (safeTaxRate / 100);
  const gst = subtotal * (safeGstRate / 100);
  const total = subtotal + tax + gst;
  return { subtotal, tax, gst, total };
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// --- Geofencing / Zone Types ---
export type ZoneType = 'work' | 'home' | 'other';

export interface Zone {
  id: string;
  name: string;
  type: ZoneType;
  lat: number;
  lng: number;
  radiusMeters: number; // typically 100–200m
  createdAt: string;
}

export type TimeLogStatus = 'active' | 'completed' | 'to_be_confirmed';
export type TimeLogCategory = 'work' | 'other' | 'unconfirmed';

export interface TimeLog {
  id: string;
  zoneId: string | null;       // null = temp/unconfirmed zone
  zoneName: string;
  category: TimeLogCategory;
  status: TimeLogStatus;
  startTime: string;           // ISO string
  endTime: string | null;      // null = still active
  durationMinutes: number;
  lat: number;
  lng: number;
  isTemp: boolean;             // true = auto-created, not yet saved as zone
  createdAt: string;
}

const ZONES_KEY = 'freelance_zones';
const TIME_LOGS_KEY = 'freelance_time_logs';

// --- Zone Storage ---
export function getZones(): Zone[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(ZONES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveZone(zone: Zone): void {
  try {
    const zones = getZones();
    const idx = zones.findIndex((z) => z.id === zone.id);
    if (idx >= 0) {
      zones[idx] = zone;
    } else {
      zones.unshift(zone);
    }
    localStorage.setItem(ZONES_KEY, JSON.stringify(zones));
  } catch (err) {
    console.error('[storage] saveZone failed:', err);
    throw err;
  }
}

export function deleteZone(id: string): void {
  try {
    const zones = getZones().filter((z) => z.id !== id);
    localStorage.setItem(ZONES_KEY, JSON.stringify(zones));
  } catch (err) {
    console.error('[storage] deleteZone failed:', err);
  }
}

export function getZoneById(id: string): Zone | undefined {
  return getZones().find((z) => z.id === id);
}

// --- Time Log Storage ---
export function getTimeLogs(): TimeLog[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(TIME_LOGS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveTimeLog(log: TimeLog): void {
  try {
    const logs = getTimeLogs();
    const idx = logs.findIndex((l) => l.id === log.id);
    if (idx >= 0) {
      logs[idx] = log;
    } else {
      logs.unshift(log);
    }
    localStorage.setItem(TIME_LOGS_KEY, JSON.stringify(logs));
  } catch (err) {
    console.error('[storage] saveTimeLog failed:', err);
    throw err;
  }
}

export function deleteTimeLog(id: string): void {
  try {
    const logs = getTimeLogs().filter((l) => l.id !== id);
    localStorage.setItem(TIME_LOGS_KEY, JSON.stringify(logs));
  } catch (err) {
    console.error('[storage] deleteTimeLog failed:', err);
  }
}

export function getActiveTimeLog(): TimeLog | undefined {
  return getTimeLogs().find((l) => l.status === 'active');
}

// Haversine distance in meters
export function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) return 0;
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const result = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return isNaN(result) ? 0 : result;
}

export function findZoneForLocation(lat: number, lng: number): Zone | undefined {
  return getZones().find((z) => distanceMeters(lat, lng, z.lat, z.lng) <= z.radiusMeters);
}

export function getClientById(id: string): Client | undefined {
  return getClients().find((c) => c.id === id);
}

// --- Storage health check (diagnostics) ---
export function getStorageStats(): { invoices: number; clients: number; zones: number; timeLogs: number; templates: number; storageKb: number } {
  try {
    const keys = [INVOICES_KEY, CLIENTS_KEY, ZONES_KEY, TIME_LOGS_KEY, TEMPLATES_KEY];
    const totalBytes = keys.reduce((sum, k) => sum + (localStorage.getItem(k)?.length ?? 0), 0);
    return {
      invoices: getInvoices().length,
      clients: getClients().length,
      zones: getZones().length,
      timeLogs: getTimeLogs().length,
      templates: getTemplates().length,
      storageKb: Math.round(totalBytes / 1024),
    };
  } catch {
    return { invoices: 0, clients: 0, zones: 0, timeLogs: 0, templates: 0, storageKb: 0 };
  }
}
